// The rails. What the X-ray agent is allowed to do, in what order, and what has
// to be true before it moves on.
//
// The default entry is NOT "build a new lens". It is AUDIT THE EXISTING CATALOG:
// re-run every lens against a fresh corpus and fix what has rotted. That ordering
// is deliberate — a grammar induced from one window decays as the corpus changes,
// and a catalog of eleven lenses that nobody re-checks is worth less than five
// that get re-checked. Building is what you do when the audit is clean and the
// discovery pass has a candidate worth the cost.
//
// Each stage has a machine-checkable postcondition. They are gates, not
// suggestions: a stage that cannot show its postcondition has not finished, and
// the agent's judgment applies WITHIN a stage rather than to whether the stage is
// done. That is the containment — a fixed surface and a fixed sequence, with the
// interesting decisions left free inside them.

export const STAGES = ["audit", "discover", "judge", "build", "present", "verify"];

/**
 * What each stage is for, what it may touch, and what proves it finished.
 * `gate(artifacts)` returns {ok, reasons[]} — reasons are what to do next, not
 * just what is wrong.
 */
export const LOOP = {
  audit: {
    purpose:
      "Re-run every existing lens over a FRESH corpus and read the findings. The "
      + "default entry point: a grammar induced from one window rots as the corpus "
      + "changes, and nothing else notices.",
    touches: "nothing — read only",
    gate: (a) => {
      const reasons = [];
      const reports = a.auditReports || [];
      // An EMPTY catalog has nothing to re-audit, and conflating that with "you
      // have not audited yet" blocked the very first run forever. `catalogSize` is
      // the distinction: zero lenses is a pass, N lenses with no reports is not.
      if (a.catalogSize === 0) return { ok: true, reasons: [] };
      if (!reports.length) reasons.push("run `xray-factory audit` for each lens' spec against a corpus pulled in the last week");
      for (const r of reports) {
        const errs = (r.findings || []).filter((f) => f.severity === "error");
        if (errs.length) reasons.push(`${r.lens}: ${errs.length} error finding(s) — fix before anything else, these are wrong output on screen`);
        // A yield collapse is how a lens dies quietly: the grammar still parses,
        // the substrate moved.
        if (r.yieldPct != null && r.yieldPct < 10) {
          reasons.push(`${r.lens}: under 10% corpus yield — read raw bodies that produced nothing; the door or the substrate has moved`);
        }
      }
      return { ok: reasons.length === 0, reasons };
    },
  },

  discover: {
    purpose:
      "Mine the corpus into candidate behaviours. Deterministic on purpose at this "
      + "stage — the clustering must be reproducible so the same corpus yields the "
      + "same candidates, and a judge that disagrees with itself run to run cannot "
      + "be calibrated.",
    touches: "nothing — read only",
    gate: (a) => {
      const reasons = [];
      const m = a.mining;
      if (!m) return { ok: false, reasons: ["run `xray-factory mine` over a DEPTH-WEIGHTED corpus"] };
      // Sampling dominates every other choice here, so it is gated rather than
      // documented: a uniform sample of recent requests is dominated by short
      // sessions, yields about one command per session, and makes every candidate
      // look like a singleton.
      if (!m.depthWeighted) reasons.push("corpus was not depth-weighted — resample from the deepest sessions before believing the ranking");
      if (!(m.clusters || []).length) reasons.push("no clusters above threshold — widen the window or lower --min, and say which");
      // VOLUME share, not skeleton share. This gate has been wrong twice, and both
      // times for the same reason: it measured the wrong denominator.
      //
      //   tail > clusters × 40   passed a 97%-singleton corpus by a hair
      //   singleton SKELETON share > 90%   refused a corpus with 104 clusters
      //     spread across 18 sessions and 11 endpoints
      //
      // A long tail is INHERENT to agent shell work — most commands are one-offs,
      // and each contributes one skeleton while a recurring shape contributes one
      // skeleton for many calls. So counting skeletons makes every real corpus look
      // structureless. What matters is how much of the WORK recurs.
      const clustered = (m.clusters || []).reduce((n, c) => n + (c.count || 0), 0);
      const totalCalls = m.totals?.calls || clustered + (m.tailCount || 0);
      const share = totalCalls ? clustered / totalCalls : 0;
      if (share < 0.15) {
        reasons.push(`only ${Math.round(share * 100)}% of calls fall in a recurring shape (${clustered}/${totalCalls}) — this window is nearly all one-offs. Widen it, or say plainly that this tenant's work does not repeat`);
      }
      return { ok: reasons.length === 0, reasons };
    },
  },

  judge: {
    purpose:
      "Rank candidates against each other and name them. The one stage that is "
      + "irreducibly a model call: naming a behaviour and identifying its consumer "
      + "is interpretive, and no clustering produces it.",
    touches: "nothing — read only",
    gate: (a) => {
      const reasons = [];
      const v = a.verdicts;
      if (!v) return { ok: false, reasons: ["build a judge request with buildJudgeRequest and run it"] };
      if (v.problems?.length) reasons.push(...v.problems.map((p) => `verdict set invalid: ${p}`));
      const picked = (v.verdicts || []).filter((x) => x.kind === "behaviour");
      if (!picked.length) reasons.push("no candidate judged a behaviour — that is a valid outcome; stop here and say so rather than building the least-bad one");
      // Independent approval is the failure mode: asked one at a time a model says
      // yes at a rate that makes the answer meaningless.
      if (picked.length > 5) reasons.push(`${picked.length} judged buildable — rank them and take the top few; a judge that approves everything has not chosen`);
      for (const x of picked) {
        if (!x.falsifier) reasons.push(`${x.key}: no falsifier — a verdict that cannot be checked later is a guess`);
      }
      return { ok: reasons.length === 0, reasons };
    },
  },

  build: {
    purpose:
      "Write the extractor and its manifest, inside the declared surface. Grammar "
      + "induced by READING raw commands, never from imagined shapes.",
    touches: "extractors/<id>/ and xrays/<id>.xray.js only",
    gate: (a) => {
      const reasons = [];
      if (!a.manifest?.ok) reasons.push(...(a.manifest?.problems || ["manifest missing — see validateXray"]).map((p) => `manifest: ${p}`));
      // An audit BEFORE any fixture exists is the point. Fixtures are written from
      // the same mental model that produced the blind spot, so they confirm it.
      if (!a.audit) reasons.push("audit the new extractor over the real corpus BEFORE writing fixtures");
      else {
        const errs = (a.audit.findings || []).filter((f) => f.severity === "error");
        if (errs.length) reasons.push(`${errs.length} error finding(s) from the audit — fix, then re-audit`);
        if (!a.ledgerRows) reasons.push("every defect the audit found needs a LEDGER row in test/audit.test.js, written before the fix");
      }
      for (const p of a.filesTouched || []) {
        if (p.ok === false) reasons.push(p.why);
      }
      return { ok: reasons.length === 0, reasons };
    },
  },

  present: {
    purpose:
      "Write the panel and have it judged. An agent authors several candidates that "
      + "differ in what they LEAD WITH; an ensemble ranks the RENDERED output under "
      + "one lens each. The editorial questions are a rubric, not a limit — an "
      + "earlier version of this gate required a human to confirm they had looked, "
      + "which mistook 'no checker decides this' for 'no agent decides this'.",
    touches: "extractors/<id>/, xrays/<id>.xray.js, xrays/<id>/Panel.jsx",
    gate: (a) => {
      const reasons = [];
      if (!a.presentation) return { ok: false, reasons: ["run checkPresentation over the files you wrote"] };
      for (const f of a.presentation.findings || []) reasons.push(`${f.rule} at ${f.path}${f.line ? `:${f.line}` : ""} — ${f.why}`);

      // Mechanical cleanliness is necessary and nowhere near sufficient, so the
      // ensemble is required rather than advisory.
      const j = a.panelJudging;
      if (!j) {
        reasons.push("render >=2 candidates and run the panel ensemble — buildPanelJudgeRequest per lens, then aggregatePanelVerdicts");
      } else {
        if (j.problems?.length) reasons.push(...j.problems.map((p) => `panel ensemble: ${p}`));
        if (j.allRejected) {
          reasons.push("every candidate was rejected for overstating something — author again with the findings; this is a real outcome, not a failure to grade");
        } else if (!j.winner) {
          reasons.push("no winner — the ensemble did not converge; add a candidate that differs in what it leads with");
        }
        for (const [c, why] of Object.entries(j.rejected || {})) {
          if (c === j.winner) reasons.push(`the winner was vetoed: ${why}`);
        }
      }
      return { ok: reasons.length === 0, reasons };
    },
  },

  verify: {
    purpose:
      "Show the lens moved something outside itself. Tests passing is not evidence "
      + "a reader gained anything.",
    touches: "nothing — read only",
    gate: (a) => {
      const reasons = [];
      if (a.residualBefore == null || a.residualAfter == null) {
        reasons.push("measure the per-trace feed's coverage before and after — that number is the honest score");
      } else if (!(a.residualAfter < a.residualBefore)) {
        reasons.push("residual did not shrink: the work this lens claims was already claimed, or it claims nothing");
      }
      // Absence claims are the class that cost the most, and they are cheap to
      // enumerate because they are rare.
      if (a.absenceClaims && !a.absenceVerified) {
        reasons.push("this lens renders absence claims ('no output', 'not in this trace') — search the agent's own context for each before shipping it");
      }
      return { ok: reasons.length === 0, reasons };
    },
  },
};

/**
 * Run one stage's gate.
 *
 * @returns {{stage, ok, reasons: string[], purpose: string}}
 */
export function gateFor(stage, artifacts = {}) {
  const s = LOOP[stage];
  if (!s) throw new Error(`unknown stage "${stage}" — one of ${STAGES.join(", ")}`);
  const { ok, reasons } = s.gate(artifacts);
  return { stage, ok, reasons, purpose: s.purpose };
}

/**
 * The first stage that is not satisfied, which is where the agent should be
 * working. Returns null when the whole loop is clean — which, at the default
 * entry, means the catalog is healthy and there is nothing to do.
 */
export function nextStage(artifacts = {}) {
  for (const stage of STAGES) {
    const g = gateFor(stage, artifacts);
    if (!g.ok) return g;
  }
  return null;
}
